import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, MapPin, Users, Search, Trophy, BookOpen, Users2, Filter } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to parse date without timezone issues
const formatEventDate = (dateStr, options = {}) => {
  if (!dateStr) return '';
  // Parse YYYY-MM-DD as local date, not UTC
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    ...options
  });
};

const EVENTS_BANNER = "https://images.unsplash.com/photo-1580216501592-7493ce4d6b58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxob3JzZSUyMHJ1bm5pbmclMjBmaWVsZHxlbnwwfHx8fDE3Njg2NjkyMTd8MA&ixlib=rb-4.1.0&q=85";

const eventTypeConfig = {
  show: { icon: Trophy, label: 'Horse Show', color: 'bg-amber-100 text-amber-800', borderColor: 'border-amber-300' },
  clinic: { icon: BookOpen, label: 'Clinic', color: 'bg-blue-100 text-blue-800', borderColor: 'border-blue-300' },
  meetup: { icon: Users2, label: 'Meetup', color: 'bg-green-100 text-green-800', borderColor: 'border-green-300' },
};

const EventCard = ({ event }) => {
  const navigate = useNavigate();
  const config = eventTypeConfig[event.event_type] || eventTypeConfig.show;
  const Icon = config.icon;
  const spotsLeft = event.max_participants - event.current_participants;
  const isFull = spotsLeft <= 0;
  
  return (
    <Card 
      className={`group overflow-hidden border border-stone-200 hover:shadow-lg hover:border-primary/20 transition-all duration-300 cursor-pointer ${isFull ? 'opacity-75' : ''}`}
      onClick={() => navigate(`/events/${event.id}`)}
      data-testid={`event-card-${event.id}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={event.image_url || EVENTS_BANNER}
          alt={event.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color} flex items-center gap-1`}>
            <Icon className="w-4 h-4" />
            {config.label}
          </span>
        </div>
        {isFull && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-white px-4 py-2 rounded-md font-semibold text-stone-900">SOLD OUT</span>
          </div>
        )}
      </div>
      <CardContent className="p-6">
        <h3 className="font-display text-xl font-semibold text-stone-900 mb-2 group-hover:text-primary transition-colors">
          {event.title}
        </h3>
        <p className="text-stone-600 text-sm mb-4 line-clamp-2">{event.description}</p>
        <div className="flex flex-col gap-2 text-sm text-stone-500">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            <span>{formatEventDate(event.date)} at {event.time}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <span>{event.location}</span>
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-100">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              <span className={spotsLeft <= 5 && spotsLeft > 0 ? 'text-terracotta font-medium' : ''}>
                {isFull ? 'Full' : `${spotsLeft} spots left`}
              </span>
            </div>
            <span className="font-mono text-lg font-semibold text-primary">${event.price}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data);
        setFilteredEvents(response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    let filtered = events;
    
    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (eventTypeFilter !== 'all') {
      filtered = filtered.filter(event => event.event_type === eventTypeFilter);
    }
    
    setFilteredEvents(filtered);
  }, [searchTerm, eventTypeFilter, events]);

  return (
    <Layout>
      <SEO 
        title="Events"
        description="Browse upcoming horse shows, clinics, and meetups at Manitoulin Horse Enthusiasts Club. Register for Western Pleasure, English Pleasure, Trail, Showmanship events and more."
        url="/events"
      />
      {/* Hero Banner */}
      <section className="relative h-64 md:h-80 flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${EVENTS_BANNER})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-hunter/80 to-hunter/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            Upcoming Events
          </h1>
          <p className="text-stone-200 text-lg max-w-xl">
            Discover and register for horse shows, clinics, and community meetups
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-secondary py-6 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-stone-200"
                data-testid="events-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-stone-500" />
              <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                <SelectTrigger className="w-40 bg-white" data-testid="events-type-filter">
                  <SelectValue placeholder="Event Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="show">Horse Shows</SelectItem>
                  <SelectItem value="clinic">Clinics</SelectItem>
                  <SelectItem value="meetup">Meetups</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="h-96 animate-pulse bg-stone-100" />
              ))}
            </div>
          ) : filteredEvents.length > 0 ? (
            <>
              <p className="text-stone-600 mb-6">{filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredEvents.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </>
          ) : (
            <Card className="p-12 text-center">
              <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold text-stone-700 mb-2">
                No Events Found
              </h3>
              <p className="text-stone-500">
                {searchTerm || eventTypeFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Check back soon for new events!'}
              </p>
              {(searchTerm || eventTypeFilter !== 'all') && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => { setSearchTerm(''); setEventTypeFilter('all'); }}
                  data-testid="clear-filters-btn"
                >
                  Clear Filters
                </Button>
              )}
            </Card>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default EventsPage;
